import React, { useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { useAdminNavigation } from '@/hooks/admin/useAdminNavigation';
import { useAppDispatch, useAppSelector } from '@/store';
import { setIsSidebarCollapsed, toggleExpandedMenuId } from '@/store/slices/admin/adminUISlice';
import { selectExpandedMenuIds, selectIsSidebarCollapsed } from '@/store/selectors/adminUISelectors';
import { INavigationItem } from '@/types/admin/navigation';
import { SVG_COMPONENTS, SVG_DIMENSIONS } from '@/assets/admin';
import { ADMIN_NAV_PARENT_ICON_KEYS } from '@/constants/admin/adminNavigation';
import { AnimatedAccordion } from '@/components/common/AnimatedAccordion';
import { GlassSidebarNavItem } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';

interface AdminNavListProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

export const AdminNavList: React.FC<AdminNavListProps> = ({ currentPath, onNavigate }) => {
  const dispatch = useAppDispatch();
  const expandedMenuIds = useAppSelector(selectExpandedMenuIds);
  const isSidebarCollapsed = useAppSelector(selectIsSidebarCollapsed);
  const { navigationItems, isPathActive, getFilteredSubNavItems } = useAdminNavigation();
  const { admin } = useAdminColors();

  const handleNavigate = useCallback((path: string) => {
    onNavigate(path);
  }, [onNavigate]);

  const renderNavMenuItem = useCallback((item: INavigationItem) => {
    const isOpen = expandedMenuIds.includes(item.id);
    const isActive = isPathActive(item.path, item.subNavItems, currentPath);
    const filteredSubNavItems = getFilteredSubNavItems(item.subNavItems);
    const isLeaf = !filteredSubNavItems || filteredSubNavItems.length === 0;

    const iconKey = ADMIN_NAV_PARENT_ICON_KEYS[item.id];
    const ParentIcon = iconKey ? SVG_COMPONENTS[iconKey] : null;
    const parentIconNode = ParentIcon ? (
      <ParentIcon width={20} height={20} color={admin.sidebar.label} />
    ) : null;

    if (isSidebarCollapsed) {
      const handleCollapsedPress = () => {
        dispatch(setIsSidebarCollapsed(false));
        if (isLeaf) {
          handleNavigate(item.path);
        } else if (!isOpen) {
          dispatch(toggleExpandedMenuId(item.id));
        }
      };
      return (
        <GlassSidebarNavItem
          key={item.id}
          icon={parentIconNode}
          title={item.title}
          isActive={isActive || isOpen}
          iconOnly
          onPress={handleCollapsedPress}
        />
      );
    }

    if (isLeaf) {
      return (
        <GlassSidebarNavItem
          key={item.id}
          icon={parentIconNode}
          title={item.title}
          description={item.description}
          isActive={isActive}
          onPress={() => handleNavigate(item.path)}
        />
      );
    }

    const parentContainerStyle = [
      styles.parentContainer,
      {
        backgroundColor: isOpen ? admin.sidebar.itemHoverBg : 'transparent',
      },
    ];

    return (
      <AnimatedAccordion
        key={item.id}
        isExpanded={isOpen}
        onToggle={() => dispatch(toggleExpandedMenuId(item.id))}
        containerStyle={parentContainerStyle}
        headerStyle={styles.parentHeader}
        contentStyle={styles.parentChildren}
        accessibilityLabel={item.title}
        accessibilityHint={`Tap to ${isOpen ? 'collapse' : 'expand'} ${item.title} menu`}
        header={
          <View style={styles.parentHeaderSlot}>
            {parentIconNode ? <View style={styles.parentIconSlot}>{parentIconNode}</View> : null}
            <View style={styles.parentTitleSlot}>
              <Text
                style={[
                  styles.parentTitle,
                  { color: admin.sidebar.label },
                  (isOpen || isActive) && styles.parentTitleEmphasized,
                ]}
              >
                {item.title}
              </Text>
              {item.description ? (
                <Text style={[styles.parentDescription, { color: admin.sidebar.description }]}>
                  {item.description}
                </Text>
              ) : null}
            </View>
          </View>
        }
        chevron={
          <SVG_COMPONENTS.CHEVRON_DOWN
            {...SVG_DIMENSIONS.CHEVRON_DOWN}
            color={admin.sidebar.description}
          />
        }
      >
        {filteredSubNavItems.map((subItem) => {
          const isSubItemActive =
            currentPath.endsWith(subItem.path) ||
            subItem.path === `/${currentPath.split('/')[1]}`;

          return (
            <GlassSidebarNavItem
              key={subItem.path}
              title={subItem.title}
              isActive={isSubItemActive}
              onPress={() => handleNavigate(subItem.path)}
            />
          );
        })}
      </AnimatedAccordion>
    );
  }, [admin, expandedMenuIds, isSidebarCollapsed, currentPath, isPathActive, getFilteredSubNavItems, dispatch, handleNavigate]);

  return (
    <View style={styles.navListContainer}>
      <View style={styles.navList}>
        {navigationItems.map(renderNavMenuItem)}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  navListContainer: {
    width: '100%',
    flex: 1,
  },
  navList: {
    width: '100%',
    gap: 8,
  },
  parentContainer: {
    width: '100%',
    borderRadius: 12,
  },
  parentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  parentHeaderSlot: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  parentIconSlot: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  parentTitleSlot: {
    flex: 1,
    gap: 2,
  },
  parentTitle: {
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
  },
  parentTitleEmphasized: {
    fontWeight: '600',
  },
  parentDescription: {
    fontFamily: 'System',
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 18,
  },
  parentChildren: {
    gap: 4,
    paddingHorizontal: 6,
    paddingBottom: 8,
  },
});
